/**
 * useOCR Hook
 *
 * React hook for OCR functionality in the Electron app.
 * Provides a simple interface to extract and parse document data.
 */

import { useState, useCallback } from 'react';
import { ParsedDocument, parseDocument } from '../utils/documentParser';
// Type declarations for window.electronAPI are in src/types/electron.d.ts

export interface OCRState {
  isProcessing: boolean;
  error: string | null;
  result: ParsedDocument | null;
}

export interface UseOCRReturn {
  /** Current OCR processing state */
  state: OCRState;
  /** Whether the app is running in Electron and OCR is available */
  isAvailable: boolean;
  /** Process an image and extract document data */
  processImage: (imageBase64: string) => Promise<ParsedDocument | null>;
  /** Reset the OCR state */
  reset: () => void;
}

/**
 * Check if running in Electron environment
 */
function isElectron(): boolean {
  return typeof window !== 'undefined' &&
    typeof window.electronAPI !== 'undefined' &&
    typeof window.electronAPI.isElectron === 'function' &&
    window.electronAPI.isElectron();
}

/**
 * useOCR hook for document text extraction in Electron
 */
export function useOCR(): UseOCRReturn {
  const [state, setState] = useState<OCRState>({
    isProcessing: false,
    error: null,
    result: null,
  });

  const isAvailable = isElectron();

  const processImage = useCallback(async (imageBase64: string): Promise<ParsedDocument | null> => {
    if (!isAvailable) {
      setState(prev => ({
        ...prev,
        error: 'OCR não disponível no navegador. Use o aplicativo desktop.',
      }));
      return null;
    }

    setState({
      isProcessing: true,
      error: null,
      result: null,
    });

    try {
      console.log('[useOCR] Processing image...');

      // Call Electron OCR API
      const ocrResult = await window.electronAPI!.extractOCR(imageBase64);

      if (!ocrResult.success) {
        throw new Error(ocrResult.error || 'Falha ao extrair texto do documento');
      }

      console.log('[useOCR] OCR extraction successful. Confidence:', ocrResult.confidence);
      console.log('[useOCR] Raw text:', ocrResult.text?.substring(0, 200) + '...');

      // Parse the extracted text
      const parsedDocument = parseDocument(ocrResult.text || '', ocrResult.confidence || 0);

      console.log('[useOCR] Parsed document:', {
        firstName: parsedDocument.firstName,
        lastName: parsedDocument.lastName,
        cpf: parsedDocument.cpf,
        rg: parsedDocument.rg,
        birthDate: parsedDocument.birthDate,
        documentType: parsedDocument.documentType,
        confidence: parsedDocument.confidence,
      });

      setState({
        isProcessing: false,
        error: null,
        result: parsedDocument,
      });

      return parsedDocument;
    } catch (error) {
      console.error('[useOCR] Error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido ao processar imagem';

      setState({
        isProcessing: false,
        error: errorMessage,
        result: null,
      });

      return null;
    }
  }, [isAvailable]);

  const reset = useCallback(() => {
    setState({
      isProcessing: false,
      error: null,
      result: null,
    });
  }, []);

  return {
    state,
    isAvailable,
    processImage,
    reset,
  };
}

export default useOCR;
